/**
 * Inventory API surface (M3): what is on the shelf, what it is worth, and who may
 * correct it.
 *
 * Money is a string the whole way, as everywhere else: the API sends a Postgres NUMERIC
 * as text because it does not fit in a double, and the only place it becomes a number is
 * `formatMoney`, at the last moment before a human reads it. `avg_cost` carries four
 * decimals rather than two — it is a weighted average, not a price — so it gets its own
 * formatter rather than being rounded into the money one.
 *
 * Quantities are numbers. They are integers by construction (the column is an `int` and
 * the ledger sums deltas), so there is nothing for a double to lose.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Envelope, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'
import { formatCost, formatMoney } from '@/lib/money'

// --- catalog ---------------------------------------------------------------------------
// The catalog has no feature folder of its own. Its screens arrive with stock, because a
// product list nobody can see the stock position on is a spreadsheet, so both live here.

export const PRICING_MODES = ['fixed', 'margin'] as const
export type PricingMode = (typeof PRICING_MODES)[number]
export const MARGIN_BASES = ['cost', 'base_price'] as const
export type MarginBasis = (typeof MARGIN_BASES)[number]
export const MARGIN_TYPES = ['percent', 'amount'] as const
export type MarginType = (typeof MARGIN_TYPES)[number]

/** Where a product discount can apply. "online" is the marketplace storefront. */
export const DISCOUNT_CHANNELS = ['pos', 'online', 'sales_order'] as const
export type DiscountChannel = (typeof DISCOUNT_CHANNELS)[number]
export const DISCOUNT_CHANNEL_LABELS: Record<DiscountChannel, string> = {
  pos: 'POS (till)',
  online: 'Online store',
  sales_order: 'Sales orders',
}

/**
 * One offer on a product. Several may overlap (a Dussehra and a Diwali offer); on any day
 * the biggest one that is running at that channel and location applies.
 */
export interface ProductDiscount {
  /** What the manager calls it ("Diwali offer"). ERP only. */
  name: string | null
  pct: string
  /** ISO days, both inclusive, in the business's timezone. Null leaves that side open. */
  starts_on: string | null
  ends_on: string | null
  channels: DiscountChannel[]
  /** Empty: every location. */
  location_ids: string[]
}

/** Today as the business's calendar sees it, `YYYY-MM-DD` (the browser's local day). */
export function todayIso(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Whether an offer runs on `day` (`YYYY-MM-DD`). ISO days compare as strings. */
export function discountIsLive(entry: Pick<ProductDiscount, 'starts_on' | 'ends_on'>, day: string) {
  return (!entry.starts_on || entry.starts_on <= day) && (!entry.ends_on || day <= entry.ends_on)
}

/**
 * The percentage off on `channel`, at `locationId`, on `day`: the biggest live offer, or 0.
 * Mirrors `promo_pct` in the API's catalog/pricing.py -- an empty location list means
 * everywhere, and a limited offer never applies where no location is known yet.
 */
export function discountPct(
  product: { discount_enabled?: boolean; discounts?: ProductDiscount[] | null },
  channel: DiscountChannel,
  locationId: string | null,
  day: string = todayIso(),
): number {
  if (product.discount_enabled !== true) return 0
  return (product.discounts ?? []).reduce((best, entry) => {
    const pct = Number(entry.pct)
    const applies =
      pct > 0 &&
      entry.channels.includes(channel) &&
      discountIsLive(entry, day) &&
      (entry.location_ids.length === 0 ||
        (locationId !== null && entry.location_ids.includes(locationId)))
    return applies && pct > best ? pct : best
  }, 0)
}

/** What margin mode charges: the basis marked up by a percentage or a flat amount. */
export function marginPrice(basis: number, type: MarginType, value: number): number {
  const raw = type === 'percent' ? (basis * (100 + value)) / 100 : basis + value
  return Math.round(raw * 100) / 100
}

export interface Product {
  id: string
  code: string
  name: string
  description: string | null
  /** The full write-up shown under the one-line description. Plain text, blank-line paragraphs. */
  long_description?: string | null
  category_id: string
  category_name: string | null
  unit_of_measure: string
  price: string
  tax_rate: string
  tax_code: string | null
  status: string
  listed_on_marketplace: boolean
  /** The discount switch and offers. Visible to sellers: a quote has to say the price is
   *  discounted. Switching off pauses every offer without losing them. */
  discount_enabled: boolean
  discounts: ProductDiscount[]
  /** Signed and short-lived, ready for a `src`. The durable half is `image_keys`. */
  images: string[] | null
  /** The storage keys behind `images` -- what a save writes back. Null for a caller the
   *  API does not show the editable half of the product to. */
  image_keys: string[] | null
  /** Null means "not yours to see" rather than unset: the API omits cost for a caller
   *  without update rights on the catalog, so margin cannot leak to a seller. */
  cost: string | null
  /** Margin fields ride with cost: margin over cost is cost by another name. */
  pricing_mode: PricingMode | null
  margin_basis: MarginBasis | null
  margin_type: MarginType | null
  margin_value: string | null
  base_price: string | null
  min_stock: number | null
  max_stock: number | null
  lead_time_days: number | null
}

export interface Category {
  id: string
  name: string
  description: string | null
  parent_id: string | null
  sort_order: number
  children: Category[]
}

export interface ProductFilters {
  q?: string
  category_id?: string
  status?: string
  page?: number
  page_size?: number
}

export const listProducts = (filters: ProductFilters = {}) => {
  const query = new URLSearchParams({
    page: String(filters.page ?? 1),
    page_size: String(filters.page_size ?? 25),
  })
  if (filters.q) query.set('q', filters.q)
  if (filters.category_id) query.set('category_id', filters.category_id)
  if (filters.status) query.set('status', filters.status)
  return apiFetch<Page<Product>>(`${API_PREFIX}/products?${query}`)
}

export const getProduct = (id: string) => apiGet<Product>(`${API_PREFIX}/products/${id}`)

export const exportProducts = (filters: ProductFilters, format: ExportFormat) => {
  const query = new URLSearchParams({ fmt: format })
  if (filters.q) query.set('q', filters.q)
  if (filters.category_id) query.set('category_id', filters.category_id)
  if (filters.status) query.set('status', filters.status)
  return apiBlob(`${API_PREFIX}/products/export?${query}`)
}

/** The API's ceiling on `page_size`. Asking for more is a 422, not a bigger page. */
const MAX_PAGE_SIZE = 200

/**
 * The whole active catalog, for screens that have to turn a product id into a name.
 *
 * Inventory does not import the catalog on the server -- an import contract forbids it, so
 * a balance and a valuation line carry ids and nothing else -- which leaves the lookup to
 * be done here. Paged rather than one huge request, and capped: past a few thousand
 * products this stops being a dropdown problem and starts being a search endpoint, and
 * silently fetching forever would hide that day rather than announce it.
 */
export async function listAllProducts(limitPages = 10): Promise<Product[]> {
  const all: Product[] = []
  for (let page = 1; page <= limitPages; page += 1) {
    const result = await listProducts({ status: 'active', page, page_size: MAX_PAGE_SIZE })
    all.push(...result.data)
    if (page >= result.meta.total_pages) break
  }
  return all
}

export const listCategories = () => apiGet<Category[]>(`${API_PREFIX}/categories`)

/** Every field optional except what a new SKU cannot be priced or classified without. */
export interface ProductInput {
  code: string
  name: string
  description?: string | null
  long_description?: string | null
  category_id: string
  unit_of_measure?: string
  /** Omitted in margin mode: the API derives it. */
  price?: string
  cost: string
  pricing_mode?: PricingMode
  margin_basis?: MarginBasis | null
  margin_type?: MarginType | null
  margin_value?: string | null
  base_price?: string | null
  discount_enabled?: boolean
  /** Replaces the whole stored list. */
  discounts?: ProductDiscount[]
  tax_rate?: string
  tax_code?: string | null
  min_stock?: number
  max_stock?: number
  lead_time_days?: number
  status?: string
  listed_on_marketplace?: boolean
  /** Storage keys from `uploadFile`, not URLs: a signed URL saved here expires. */
  images?: string[] | null
}

export const createProduct = (input: ProductInput) =>
  apiGet<Product>(`${API_PREFIX}/products`, { method: 'POST', body: JSON.stringify(input) })

export const updateProduct = (id: string, input: Partial<ProductInput>) =>
  apiGet<Product>(`${API_PREFIX}/products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

/**
 * Puts a selection of products on the storefront, or takes them off it, in one request.
 *
 * One flag for the whole selection, matching `ProductListingBulkUpdate` on the API. The
 * server does it in a single transaction, so the selection either all moves or none of it
 * does -- which is the point of having the route at all: a loop of PATCHes that fails
 * halfway leaves the catalog in a state nobody asked for and the screen unable to say
 * which half went through. Capped at 500 ids by the server.
 */
export const setProductsListed = (product_ids: string[], listed: boolean) =>
  apiGet<Product[]>(`${API_PREFIX}/products/bulk/marketplace-listing`, {
    method: 'POST',
    body: JSON.stringify({ product_ids, listed }),
  })

/** Discontinues the product (soft delete) -- see the router's own summary for why nothing
 *  here is ever truly removed. */
export const deleteProduct = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/products/${id}`, { method: 'DELETE' })

export interface CategoryInput {
  name: string
  description?: string | null
  parent_id?: string | null
  sort_order?: number
}

export interface CategoryLeaf {
  id: string
  name: string
  description: string | null
  parent_id: string | null
  sort_order: number
}

export const createCategory = (input: CategoryInput) =>
  apiGet<CategoryLeaf>(`${API_PREFIX}/categories`, { method: 'POST', body: JSON.stringify(input) })

export const updateCategory = (id: string, input: Partial<CategoryInput>) =>
  apiGet<CategoryLeaf>(`${API_PREFIX}/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

/** Refused by the API while the category still has subcategories or products in it -- see
 *  `catalog.service.delete_category`. Hard delete: categories have no soft-delete column. */
export const deleteCategory = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/categories/${id}`, { method: 'DELETE' })

/** Flattened for a filter dropdown: the tree is the shape the API sends, not the shape a
 *  `<select>` wants. Depth is kept so a child can be indented under its parent. */
export function flattenCategories(nodes: Category[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${'— '.repeat(depth)}${node.name}` },
    ...flattenCategories(node.children ?? [], depth + 1),
  ])
}

// --- tax rules ---------------------------------------------------------------------------
// Product- or category-scoped overrides of a product's own `tax_rate`, effective-dated so a
// rate change never rewrites how an old order was priced. See `catalog.service.resolve_tax_rule`.

export interface TaxRule {
  id: string
  name: string
  rate: string
  product_id: string | null
  category_id: string | null
  valid_from: string
  valid_to: string | null
  /** Resolved by the server for display; never used to price anything. */
  product_code: string | null
  product_name: string | null
  category_name: string | null
}

export interface TaxRuleFilters {
  product_id?: string
  category_id?: string
  active_on?: string
}

export const listTaxRules = (filters: TaxRuleFilters = {}) => {
  const query = new URLSearchParams()
  if (filters.product_id) query.set('product_id', filters.product_id)
  if (filters.category_id) query.set('category_id', filters.category_id)
  if (filters.active_on) query.set('active_on', filters.active_on)
  const qs = query.toString()
  return apiGet<TaxRule[]>(`${API_PREFIX}/tax-rules${qs ? `?${qs}` : ''}`)
}

/** A rule applies to a product, a category, or neither (a catch-all) -- never both. */
export interface TaxRuleInput {
  name: string
  rate: string
  product_id?: string | null
  category_id?: string | null
  valid_from: string
  valid_to?: string | null
}

export const createTaxRule = (input: TaxRuleInput) =>
  apiGet<TaxRule>(`${API_PREFIX}/tax-rules`, { method: 'POST', body: JSON.stringify(input) })

/** The only edit a tax rule takes after creation: closing its effective window. Rate and
 *  scope are immutable, so an old order always reprices at the rate that was live when it
 *  was raised -- see `catalog.service.update_tax_rule`. */
export const closeTaxRule = (id: string, valid_to: string) =>
  apiGet<TaxRule>(`${API_PREFIX}/tax-rules/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ valid_to }),
  })

/** Only accepted while the rule has not yet taken effect -- see the router's own summary. */
export const deleteTaxRule = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/tax-rules/${id}`, { method: 'DELETE' })

// --- locations and balances --------------------------------------------------------------

export const LOCATION_TYPES = ['store', 'warehouse', 'online'] as const
export type LocationType = (typeof LOCATION_TYPES)[number]

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  store: 'Store',
  warehouse: 'Warehouse',
  online: 'Online',
}

export const LOCATION_STATUSES = ['active', 'inactive'] as const
export type LocationStatus = (typeof LOCATION_STATUSES)[number]

export interface StockLocation {
  id: string
  code: string
  name: string
  type: string
  status: string
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  phone: string | null
  latitude: string | null
  longitude: string | null
  manager_id: string | null
}

export const listLocations = () =>
  apiFetch<Page<StockLocation>>(`${API_PREFIX}/locations?page_size=100`)

export interface LocationCreateInput {
  code: string
  name: string
  type: LocationType
  status: LocationStatus
  address: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  phone: string | null
  latitude: number | null
  longitude: number | null
  manager_id: string | null
}

export type LocationUpdateInput = Partial<Omit<LocationCreateInput, 'code'>>

export const createLocation = (input: LocationCreateInput) =>
  apiFetch<Envelope<StockLocation>>(`${API_PREFIX}/locations`, {
    method: 'POST',
    body: JSON.stringify(input),
  }).then((res) => res.data)

export const updateLocation = (id: string, input: LocationUpdateInput) =>
  apiFetch<Envelope<StockLocation>>(`${API_PREFIX}/locations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  }).then((res) => res.data)

export interface Balance {
  id: string
  product_id: string
  location_id: string
  quantity: number
  reserved_quantity: number
  /** quantity - reserved. What a new order may actually take. */
  available: number
  reorder_point: number
  avg_cost: string
  stock_value: string
  last_count_date: string | null
  updated_at: string
}

export interface BalanceFilters {
  product_id?: string
  location_id?: string
  low_stock?: boolean
  in_stock_only?: boolean
  page?: number
}

export const listBalances = (filters: BalanceFilters = {}) => {
  const query = new URLSearchParams({ page: String(filters.page ?? 1), page_size: '50' })
  if (filters.product_id) query.set('product_id', filters.product_id)
  if (filters.location_id) query.set('location_id', filters.location_id)
  if (filters.low_stock) query.set('low_stock', 'true')
  if (filters.in_stock_only) query.set('in_stock_only', 'true')
  return apiFetch<Page<Balance>>(`${API_PREFIX}/inventory?${query}`)
}

export const exportBalances = (filters: BalanceFilters, format: ExportFormat) => {
  const query = new URLSearchParams({ fmt: format })
  if (filters.product_id) query.set('product_id', filters.product_id)
  if (filters.location_id) query.set('location_id', filters.location_id)
  if (filters.low_stock) query.set('low_stock', 'true')
  if (filters.in_stock_only) query.set('in_stock_only', 'true')
  return apiBlob(`${API_PREFIX}/inventory/export?${query}`)
}

export const listLowStock = (locationId?: string) =>
  apiGet<Balance[]>(
    `${API_PREFIX}/inventory/low-stock${locationId ? `?location_id=${locationId}` : ''}`,
  )

export const setReorderPoint = (productId: string, locationId: string, reorderPoint: number) =>
  apiGet<Balance>(`${API_PREFIX}/inventory/${productId}/${locationId}/settings`, {
    method: 'PUT',
    body: JSON.stringify({ reorder_point: reorderPoint }),
  })

// --- movements ----------------------------------------------------------------------------

export const MOVEMENT_REASONS = [
  'purchase',
  'sale',
  'return',
  'adjustment',
  'transfer_in',
  'transfer_out',
  'damage',
  'opening_balance',
] as const

export type MovementReason = (typeof MOVEMENT_REASONS)[number]

export const REASON_LABELS: Record<MovementReason, string> = {
  purchase: 'Goods received',
  sale: 'Sold',
  return: 'Customer return',
  adjustment: 'Correction',
  transfer_in: 'Transferred in',
  transfer_out: 'Transferred out',
  damage: 'Damage',
  opening_balance: 'Opening balance',
}

export interface Movement {
  id: string
  product_id: string
  location_id: string
  qty_delta: number
  reason: MovementReason
  ref_type: string | null
  ref_id: string | null
  unit_cost: string | null
  performed_by: string | null
  notes: string | null
  created_at: string
}

export interface MovementFilters {
  product_id?: string
  location_id?: string
  reason?: string
  page?: number
}

export const listMovements = (filters: MovementFilters = {}) => {
  const query = new URLSearchParams({ page: String(filters.page ?? 1), page_size: '25' })
  if (filters.product_id) query.set('product_id', filters.product_id)
  if (filters.location_id) query.set('location_id', filters.location_id)
  if (filters.reason) query.set('reason', filters.reason)
  return apiFetch<Page<Movement>>(`${API_PREFIX}/inventory/movements?${query}`)
}

export const exportMovements = (filters: MovementFilters, format: ExportFormat) => {
  const query = new URLSearchParams({ fmt: format })
  if (filters.product_id) query.set('product_id', filters.product_id)
  if (filters.location_id) query.set('location_id', filters.location_id)
  if (filters.reason) query.set('reason', filters.reason)
  return apiBlob(`${API_PREFIX}/inventory/movements/export?${query}`)
}

// --- valuation ------------------------------------------------------------------------------

export interface ValuationLine {
  product_id: string
  location_id: string
  quantity: number
  avg_cost: string
  value: string
}

export interface Valuation {
  lines: ValuationLine[]
  total_quantity: number
  total_value: string
}

export const getValuation = (locationId?: string) =>
  apiGet<Valuation>(
    `${API_PREFIX}/inventory/valuation${locationId ? `?location_id=${locationId}` : ''}`,
  )

// --- the correction queue ---------------------------------------------------------------------

export const ADJUSTMENT_REASONS = ['adjustment', 'damage'] as const

export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number]

export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReason, string> = {
  adjustment: 'Correction after a count',
  damage: 'Damaged or expired',
}

export type AdjustmentStatus = 'pending' | 'approved' | 'rejected'

export const ADJUSTMENT_STATUS_LABELS: Record<AdjustmentStatus, string> = {
  pending: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
}

export interface Adjustment {
  id: string
  product_id: string
  location_id: string
  qty_delta: number
  reason: AdjustmentReason
  unit_cost: string | null
  /** What it was worth when it was raised — the figure the limit was applied to, not
   *  what the same units would be worth at today's average. */
  value: string
  status: AdjustmentStatus
  requested_by: string | null
  decided_by: string | null
  decided_at: string | null
  decision_note: string | null
  movement_id: string | null
  notes: string | null
  created_at: string
}

export interface AdjustmentInput {
  product_id: string
  location_id: string
  qty_delta: number
  reason: AdjustmentReason
  unit_cost?: string | null
  notes?: string | null
}

/**
 * Raise a correction. Under the company's limit it posts on the spot; above it, nothing
 * moves until somebody else approves — which is why the caller has to read `status` off
 * the response rather than assume the stock changed.
 */
export const requestAdjustment = (input: AdjustmentInput) =>
  apiGet<Adjustment>(`${API_PREFIX}/inventory/adjustments`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export interface AdjustmentFilters {
  status?: AdjustmentStatus | ''
  location_id?: string
  product_id?: string
  page?: number
}

export const listAdjustments = (filters: AdjustmentFilters = {}) => {
  const query = new URLSearchParams({ page: String(filters.page ?? 1), page_size: '25' })
  if (filters.status) query.set('status', filters.status)
  if (filters.location_id) query.set('location_id', filters.location_id)
  if (filters.product_id) query.set('product_id', filters.product_id)
  return apiFetch<Page<Adjustment>>(`${API_PREFIX}/inventory/adjustments?${query}`)
}

export const approveAdjustment = (id: string) =>
  apiGet<Adjustment>(`${API_PREFIX}/inventory/adjustments/${id}/approve`, { method: 'POST' })

export const rejectAdjustment = (id: string, note?: string) =>
  apiGet<Adjustment>(`${API_PREFIX}/inventory/adjustments/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note: note || null }),
  })

// --- formatting --------------------------------------------------------------------------------

/** Re-exported so a screen in this feature has one import for its figures. `formatCost`
 *  carries four decimals because a weighted average is not a price -- see `@/lib/money`. */
export { formatCost, formatMoney }

/** A movement or correction, signed the way a stock person reads it: +12, -3. */
export const formatDelta = (delta: number) => `${delta > 0 ? '+' : ''}${delta}`
